import React, { useState } from 'react';
import styled, { keyframes, css } from 'styled-components';
import { useLanguage } from '../../hooks/useLanguage';
import { getTranslation } from '../../translations/presentation';

const particleFloat = keyframes`
  0%, 100% {
    transform: translateY(0) translateX(0) scale(1);
    opacity: 0.4;
  }
  50% {
    transform: translateY(-40px) translateX(30px) scale(1.2);
    opacity: 1;
  }
`;

const hexagonFloat = keyframes`
  0%, 100% {
    transform: translateY(0) rotate(0deg);
    opacity: 0.15;
  }
  50% {
    transform: translateY(-25px) rotate(180deg);
    opacity: 0.4;
  }
`;

const glowPulse = keyframes`
  0%, 100% {
    box-shadow: 
      0 0 40px rgba(100, 182, 255, 0.3),
      0 0 80px rgba(0, 255, 255, 0.15),
      0 0 120px rgba(100, 182, 255, 0.1),
      inset 0 0 40px rgba(100, 182, 255, 0.08);
  }
  50% {
    box-shadow: 
      0 0 60px rgba(100, 182, 255, 0.5),
      0 0 120px rgba(0, 255, 255, 0.3),
      0 0 180px rgba(100, 182, 255, 0.2),
      inset 0 0 60px rgba(100, 182, 255, 0.15);
  }
`;

const scanline = keyframes`
  0% {
    transform: translateY(-100%);
    opacity: 0;
  }
  10% {
    opacity: 1;
  }
  90% {
    opacity: 1;
  }
  100% {
    transform: translateY(100vh);
    opacity: 0;
  }
`;

const cornerExpand = keyframes`
  0%, 100% {
    width: 50px;
    height: 50px;
    opacity: 0.7;
  }
  50% {
    width: 70px;
    height: 70px;
    opacity: 1;
  }
`;

const gradientShift = keyframes`
  0% {
    background-position: 0% 50%;
  }
  50% {
    background-position: 100% 50%;
  }
  100% {
    background-position: 0% 50%;
  }
`;

const dataFlowLeft = keyframes`
  0% {
    transform: translateX(100%);
    opacity: 0;
  }
  10% {
    opacity: 1;
  }
  90% {
    opacity: 1;
  }
  100% {
    transform: translateX(-100%);
    opacity: 0;
  }
`;

const dataFlowRight = keyframes`
  0% {
    transform: translateX(-100%);
    opacity: 0;
  }
  10% {
    opacity: 1;
  }
  90% {
    opacity: 1;
  }
  100% {
    transform: translateX(100%);
    opacity: 0;
  }
`;

const hologramFlicker = keyframes`
  0%, 100% {
    opacity: 1;
    filter: brightness(1);
  }
  50% {
    opacity: 0.95;
    filter: brightness(1.1);
  }
  75% {
    opacity: 0.98;
    filter: brightness(1.05);
  }
`;

const perspectivePulse = keyframes`
  0%, 100% {
    transform: perspective(1000px) rotateX(0deg) rotateY(0deg) translateZ(0px);
  }
  25% {
    transform: perspective(1000px) rotateX(2deg) rotateY(-2deg) translateZ(10px);
  }
  75% {
    transform: perspective(1000px) rotateX(-2deg) rotateY(2deg) translateZ(10px);
  }
`;

const energyPulse = keyframes`
  0%, 100% {
    transform: scale(1);
    opacity: 0.3;
  }
  50% {
    transform: scale(1.5);
    opacity: 0;
  }
`;

const circuitFlow = keyframes`
  0% {
    stroke-dashoffset: 1000;
    opacity: 0;
  }
  10% {
    opacity: 1;
  }
  90% {
    opacity: 1;
  }
  100% {
    stroke-dashoffset: 0;
    opacity: 0;
  }
`;

const textReveal = keyframes`
  0% {
    opacity: 0;
    transform: translateY(20px);
    filter: blur(10px);
  }
  100% {
    opacity: 1;
    transform: translateY(0);
    filter: blur(0);
  }
`;

const slideInFromLeft = keyframes`
  0% {
    opacity: 0;
    transform: translateX(-150px) rotateY(-90deg);
    filter: blur(20px);
  }
  100% {
    opacity: 1;
    transform: translateX(0) rotateY(0deg);
    filter: blur(0);
  }
`;

const slideInFromRight = keyframes`
  0% {
    opacity: 0;
    transform: translateX(150px) rotateY(90deg);
    filter: blur(20px);
  }
  100% {
    opacity: 1;
    transform: translateX(0) rotateY(0deg);
    filter: blur(0);
  }
`;

const digitalReveal = keyframes`
  0% {
    opacity: 0;
    transform: scale(0.5) rotateZ(-10deg);
    filter: blur(30px) brightness(3);
  }
  50% {
    filter: blur(5px) brightness(1.5);
  }
  100% {
    opacity: 1;
    transform: scale(1) rotateZ(0deg);
    filter: blur(0) brightness(1);
  }
`;

const quantumPulse = keyframes`
  0%, 100% {
    transform: scale(1) rotate(0deg);
    opacity: 0.8;
  }
  25% {
    transform: scale(1.05) rotate(1deg);
    opacity: 1;
  }
  50% {
    transform: scale(0.95) rotate(-1deg);
    opacity: 0.9;
  }
  75% {
    transform: scale(1.02) rotate(0.5deg);
    opacity: 1;
  }
`;

const matrixRain = keyframes`
  0% {
    transform: translateY(-100%);
    opacity: 0;
  }
  10% {
    opacity: 1;
  }
  90% {
    opacity: 0.8;
  }
  100% {
    transform: translateY(100vh);
    opacity: 0;
  }
`;

const particleOrbit = keyframes`
  0% {
    transform: rotate(0deg) translateX(200px) rotate(0deg);
    opacity: 0.3;
  }
  50% {
    opacity: 1;
  }
  100% {
    transform: rotate(360deg) translateX(200px) rotate(-360deg);
    opacity: 0.3;
  }
`;

const glitchEffect = keyframes`
  0%, 90%, 100% {
    transform: translate(0);
    opacity: 1;
  }
  92% {
    transform: translate(-2px, 2px);
    opacity: 0.8;
  }
  94% {
    transform: translate(2px, -2px);
    opacity: 0.9;
  }
  96% {
    transform: translate(-1px, 1px);
    opacity: 0.95;
  }
`;

const SlideWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 100%;
  width: 100%;
  padding: 40px;
  overflow: hidden;
  position: relative;
  perspective: 3000px;
  cursor: pointer;
  user-select: none;
  background: radial-gradient(ellipse at 50% 50%, rgba(0, 20, 40, 0.4) 0%, rgba(0, 5, 15, 0.8) 100%);
  
  &:active {
    transform: scale(0.998);
  }
`;

const TechGrid = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-image: 
    linear-gradient(rgba(100, 182, 255, 0.02) 1px, transparent 1px),
    linear-gradient(90deg, rgba(100, 182, 255, 0.02) 1px, transparent 1px),
    radial-gradient(circle at 30% 40%, rgba(0, 255, 255, 0.05) 0%, transparent 50%),
    radial-gradient(circle at 70% 60%, rgba(100, 182, 255, 0.03) 0%, transparent 50%);
  background-size: 80px 80px, 80px 80px, 100% 100%, 100% 100%;
  pointer-events: none;
  z-index: 1;
  opacity: 0.8;
`;

const Scanline = styled.div`
  position: absolute;
  top: -100%;
  left: 0;
  right: 0;
  height: 150px;
  background: linear-gradient(
    to bottom,
    transparent,
    rgba(0, 255, 255, 0.15),
    rgba(100, 182, 255, 0.1),
    transparent
  );
  pointer-events: none;
  animation: ${css`${scanline}`} 10s linear infinite;
  z-index: 2;
  filter: blur(2px);
`;

const FloatingParticles = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  pointer-events: none;
  z-index: 2;
`;

const Particle = styled.div`
  position: absolute;
  width: ${props => props.size}px;
  height: ${props => props.size}px;
  background: ${props => props.color};
  border-radius: 50%;
  animation: ${css`${particleFloat}`} ${props => props.duration}s ease-in-out infinite;
  animation-delay: ${props => props.delay}s;
  top: ${props => props.top}%;
  left: ${props => props.left}%;
  opacity: 0.4;
  filter: blur(1px);
  box-shadow: 0 0 10px ${props => props.color};
`;

const HexagonLayer = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  pointer-events: none;
  z-index: 1;
`;

const Hexagon = styled.div`
  position: absolute;
  width: ${props => props.size}px;
  height: ${props => props.size * 1.15}px;
  top: ${props => props.top}%;
  left: ${props => props.left}%;
  animation: ${css`${hexagonFloat}`} ${props => props.duration}s ease-in-out infinite;
  animation-delay: ${props => props.delay}s;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: transparent;
    border: 1px solid ${props => props.color};
    clip-path: polygon(50% 0%, 100% 25%, 100% 75%, 50% 100%, 0% 75%, 0% 25%);
  }
`;

const EnergyRing = styled.div`
  position: absolute;
  width: 400px;
  height: 400px;
  border: 2px solid rgba(0, 255, 255, 0.3);
  border-radius: 50%;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  pointer-events: none;
  z-index: 1;
  animation: ${css`${energyPulse}`} 4s ease-in-out infinite;
  
  &::before {
    content: '';
    position: absolute;
    top: -2px;
    left: -2px;
    right: -2px;
    bottom: -2px;
    border: 2px solid rgba(100, 182, 255, 0.2);
    border-radius: 50%;
    animation: ${css`${energyPulse}`} 4s ease-in-out infinite 0.5s;
  }
  
  &::after {
    content: '';
    position: absolute;
    top: -4px;
    left: -4px;
    right: -4px;
    bottom: -4px;
    border: 2px solid rgba(0, 255, 255, 0.15);
    border-radius: 50%;
    animation: ${css`${energyPulse}`} 4s ease-in-out infinite 1s;
  }
  
  @media (max-width: 768px) {
    width: 250px;
    height: 250px;
  }
`;

const ContentPreview = styled.div`
  max-width: 1400px;
  width: 92%;
  margin: 0 auto;
  padding: 80px 100px;
  border: 1px solid rgba(100, 182, 255, 0.3);
  border-radius: 32px;
  background: linear-gradient(135deg, rgba(0, 10, 25, 0.95) 0%, rgba(0, 5, 15, 0.98) 100%);
  backdrop-filter: blur(60px) saturate(180%);
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  z-index: 3;
  animation: ${css`${glowPulse}`} 8s ease-in-out infinite;
  overflow: hidden;
  transform-style: preserve-3d;
  box-shadow: 
    0 30px 90px rgba(0, 0, 0, 0.7),
    0 0 120px rgba(100, 182, 255, 0.15),
    0 0 200px rgba(0, 255, 255, 0.08),
    inset 0 0 100px rgba(100, 182, 255, 0.03);
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 3px;
    background: linear-gradient(
      90deg,
      transparent,
      rgba(100, 182, 255, 1),
      rgba(0, 255, 255, 1),
      rgba(100, 182, 255, 1),
      transparent
    );
    background-size: 200% 100%;
    animation: ${css`${gradientShift}`} 3s linear infinite;
    box-shadow: 0 0 25px rgba(0, 255, 255, 1), 0 5px 30px rgba(0, 255, 255, 0.5);
  }
  
  &::after {
    content: '';
    position: absolute;
    bottom: 0;
    left: 0;
    right: 0;
    height: 3px;
    background: linear-gradient(
      90deg,
      transparent,
      rgba(0, 255, 255, 1),
      rgba(100, 182, 255, 1),
      rgba(0, 255, 255, 1),
      transparent
    );
    background-size: 200% 100%;
    animation: ${css`${gradientShift}`} 3s linear infinite reverse;
    box-shadow: 0 0 25px rgba(100, 182, 255, 1), 0 -5px 30px rgba(100, 182, 255, 0.5);
  }
  
  @media (max-width: 768px) {
    padding: 50px 30px;
  }
`;

const Corner = styled.div`
  position: absolute;
  width: 50px;
  height: 50px;
  border-color: rgba(0, 255, 255, 0.9);
  border-style: solid;
  animation: ${css`${cornerExpand}`} 3s ease-in-out infinite;
  filter: drop-shadow(0 0 10px rgba(0, 255, 255, 0.6));
  
  ${props => props.position === 'top-left' && css`
    top: -2px;
    left: -2px;
    border-width: 3px 0 0 3px;
    
    &::before {
      content: '';
      position: absolute;
      top: -5px;
      left: -5px;
      width: 15px;
      height: 15px;
      background: rgba(0, 255, 255, 0.3);
      border-radius: 50%;
      box-shadow: 0 0 15px rgba(0, 255, 255, 0.8);
    }
  `}
  
  ${props => props.position === 'top-right' && css`
    top: -2px;
    right: -2px;
    border-width: 3px 3px 0 0;
    animation-delay: 0.75s;
    
    &::before {
      content: '';
      position: absolute;
      top: -5px;
      right: -5px;
      width: 15px;
      height: 15px;
      background: rgba(0, 255, 255, 0.3);
      border-radius: 50%;
      box-shadow: 0 0 15px rgba(0, 255, 255, 0.8);
    }
  `}
  
  ${props => props.position === 'bottom-left' && css`
    bottom: -2px;
    left: -2px;
    border-width: 0 0 3px 3px;
    animation-delay: 1.5s;
    
    &::before {
      content: '';
      position: absolute;
      bottom: -5px;
      left: -5px;
      width: 15px;
      height: 15px;
      background: rgba(0, 255, 255, 0.3);
      border-radius: 50%;
      box-shadow: 0 0 15px rgba(0, 255, 255, 0.8);
    }
  `}
  
  ${props => props.position === 'bottom-right' && css`
    bottom: -2px;
    right: -2px;
    border-width: 0 3px 3px 0;
    animation-delay: 2.25s;
    
    &::before {
      content: '';
      position: absolute;
      bottom: -5px;
      right: -5px;
      width: 15px;
      height: 15px;
      background: rgba(0, 255, 255, 0.3);
      border-radius: 50%;
      box-shadow: 0 0 15px rgba(0, 255, 255, 0.8);
    }
  `}
`;

const DataFlow = styled.div`
  position: absolute;
  height: 3px;
  background: linear-gradient(
    90deg,
    transparent,
    rgba(0, 255, 255, 0.8),
    rgba(100, 182, 255, 1),
    rgba(0, 255, 255, 0.8),
    transparent
  );
  width: 350px;
  box-shadow: 0 0 10px rgba(0, 255, 255, 0.6);
  filter: blur(0.5px);
  
  ${props => props.position === 'top' && css`
    top: 30%;
    left: 0;
    animation: ${dataFlowLeft} 5s linear infinite;
    animation-delay: ${props.delay}s;
  `}
  
  ${props => props.position === 'bottom' && css`
    bottom: 30%;
    right: 0;
    animation: ${dataFlowRight} 5s linear infinite;
    animation-delay: ${props.delay}s;
  `}
`;

const TitleContainer = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
  z-index: 2;
`;

const MainTitle = styled.h1`
  font-family: 'Orbitron', sans-serif;
  font-size: 2rem;
  color: rgba(255, 255, 255, 0.95);
  text-align: center;
  margin: 0 0 40px 0;
  letter-spacing: 28px;
  text-transform: uppercase;
  text-shadow: 
    0 0 30px rgba(100, 182, 255, 0.6),
    0 0 60px rgba(100, 182, 255, 0.3),
    0 0 100px rgba(0, 255, 255, 0.2),
    0 4px 20px rgba(0, 0, 0, 0.8);
  font-weight: 200;
  animation: ${css`${textReveal}`} 1.5s cubic-bezier(0.16, 1, 0.3, 1);
  position: relative;
  opacity: 0.9;
  
  &::before {
    content: 'MÉTODO';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    color: rgba(0, 255, 255, 0.4);
    filter: blur(8px);
    z-index: -1;
    animation: ${css`${quantumPulse}`} 4s ease-in-out infinite;
  }
  
  &::after {
    content: '';
    position: absolute;
    bottom: -15px;
    left: 50%;
    transform: translateX(-50%);
    width: 60%;
    height: 2px;
    background: linear-gradient(
      90deg,
      transparent,
      rgba(0, 255, 255, 0.8),
      transparent
    );
    box-shadow: 0 0 15px rgba(0, 255, 255, 0.8);
  }
  
  @media (max-width: 768px) {
    font-size: 2rem;
    letter-spacing: 8px;
  }
`;

const MethodContainer = styled.div`
  position: relative;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  margin: 20px 0;
`;

const MethodHighlight = styled.div`
  font-family: 'Orbitron', sans-serif;
  font-size: 12rem;
  font-weight: 200;
  background: linear-gradient(
    135deg,
    #ffffff 0%,
    #00FFFF 15%,
    #64B6FF 35%,
    #00FFFF 55%,
    #ffffff 70%,
    #64B6FF 85%,
    #00FFFF 100%
  );
  background-size: 400% 400%;
  animation: 
    ${css`${gradientShift}`} 8s ease infinite, 
    ${css`${perspectivePulse}`} 12s ease-in-out infinite, 
    ${css`${hologramFlicker}`} 3s ease-in-out infinite;
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  text-align: center;
  letter-spacing: 40px;
  position: relative;
  filter: 
    drop-shadow(0 0 80px rgba(0, 255, 255, 0.8))
    drop-shadow(0 0 140px rgba(0, 255, 255, 0.5))
    drop-shadow(0 0 220px rgba(100, 182, 255, 0.3))
    drop-shadow(0 15px 60px rgba(0, 0, 0, 0.8));
  transform-style: preserve-3d;
  opacity: 0.98;
  
  &::before {
    content: 'A²';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    background: linear-gradient(
      135deg,
      rgba(0, 255, 255, 0.5),
      rgba(100, 182, 255, 0.5)
    );
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    background-clip: text;
    z-index: -1;
    filter: blur(30px);
    transform: translateZ(-80px);
    animation: ${css`${quantumPulse}`} 5s ease-in-out infinite;
  }
  
  &::after {
    content: 'A²';
    position: absolute;
    top: 8px;
    left: 8px;
    right: 0;
    color: rgba(0, 255, 255, 0.2);
    z-index: -2;
    filter: blur(8px);
    animation: ${css`${glitchEffect}`} 6s ease-in-out infinite;
  }
  
  @media (max-width: 768px) {
    font-size: 7rem;
    letter-spacing: 12px;
  }
`;

const SubtitleContainer = styled.div`
  display: flex;
  gap: 25px;
  align-items: center;
  margin-top: 30px;
  
  @media (max-width: 768px) {
    flex-direction: column;
    gap: 15px;
  }
`;

const SubtitlePart = styled.div`
  font-family: 'Orbitron', sans-serif;
  font-size: 2.6rem;
  color: rgba(255, 255, 255, 0.95);
  text-align: center;
  letter-spacing: 14px;
  text-transform: uppercase;
  padding: 24px 50px;
  border: 1px solid rgba(100, 182, 255, 0.4);
  border-radius: 20px;
  background: linear-gradient(135deg, rgba(100, 182, 255, 0.06) 0%, rgba(0, 255, 255, 0.03) 100%);
  position: relative;
  overflow: hidden;
  backdrop-filter: blur(30px) saturate(150%);
  box-shadow: 
    0 0 60px rgba(0, 255, 255, 0.2),
    0 15px 60px rgba(0, 0, 0, 0.5),
    inset 0 0 50px rgba(100, 182, 255, 0.08),
    inset 0 1px 0 rgba(255, 255, 255, 0.1);
  animation: ${props => props.$fromLeft ? css`${slideInFromLeft} 1s cubic-bezier(0.16, 1, 0.3, 1)` : css`${slideInFromRight} 1s cubic-bezier(0.16, 1, 0.3, 1)`};
  animation-fill-mode: both;
  transform-style: preserve-3d;
  transition: all 0.4s cubic-bezier(0.16, 1, 0.3, 1);
  
  &:hover {
    transform: scale(1.05) translateZ(20px);
    box-shadow: 
      0 0 50px rgba(0, 255, 255, 0.5),
      0 15px 50px rgba(0, 0, 0, 0.6),
      inset 0 0 40px rgba(100, 182, 255, 0.2);
  }
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: -100%;
    width: 100%;
    height: 100%;
    background: linear-gradient(
      90deg,
      transparent,
      rgba(0, 255, 255, 0.4),
      transparent
    );
    animation: ${css`${dataFlowLeft}`} 3s linear infinite;
  }
  
  &::after {
    content: '';
    position: absolute;
    top: -3px;
    left: -3px;
    right: -3px;
    bottom: -3px;
    background: linear-gradient(
      135deg,
      rgba(0, 255, 255, 0.3),
      transparent,
      rgba(100, 182, 255, 0.3)
    );
    border-radius: 16px;
    z-index: -1;
    filter: blur(15px);
    animation: ${css`${quantumPulse}`} 4s ease-in-out infinite;
  }
  
  @media (max-width: 768px) {
    font-size: 1.6rem;
    padding: 15px 30px;
    letter-spacing: 4px;
  }
`;

const Separator = styled.div`
  width: 40px;
  height: 2px;
  background: linear-gradient(
    90deg,
    rgba(100, 182, 255, 0.6),
    rgba(0, 255, 255, 0.8),
    rgba(100, 182, 255, 0.6)
  );
  
  @media (max-width: 768px) {
    width: 2px;
    height: 30px;
  }
`;

const PresentationSlide12 = () => {
  const { lang } = useLanguage();
  const t = getTranslation('slide12', lang);
  const [clickCount, setClickCount] = useState(0);
  
  const particles = [
    { size: 4, color: 'rgba(100, 182, 255, 0.6)', top: 15, left: 20, duration: 6, delay: 0 },
    { size: 3, color: 'rgba(0, 255, 255, 0.6)', top: 30, left: 80, duration: 7, delay: 1 },
    { size: 5, color: 'rgba(100, 182, 255, 0.5)', top: 70, left: 15, duration: 8, delay: 2 },
    { size: 4, color: 'rgba(0, 255, 255, 0.5)', top: 85, left: 85, duration: 6, delay: 1.5 },
    { size: 3, color: 'rgba(100, 182, 255, 0.6)', top: 50, left: 50, duration: 7, delay: 0.5 },
    { size: 4, color: 'rgba(0, 255, 255, 0.5)', top: 25, left: 40, duration: 9, delay: 3 },
  ];

  const handleClick = () => {
    if (clickCount < 2) {
      setClickCount(prev => prev + 1);
    }
  };

  return (
    <SlideWrapper onClick={handleClick}>
      <TechGrid />
      <Scanline />
      
      <FloatingParticles>
        {particles.map((particle, index) => (
          <Particle key={index} {...particle} />
        ))}
      </FloatingParticles>
      
      <ContentPreview>
        <Corner position="top-left" />
        <Corner position="top-right" />
        <Corner position="bottom-left" />
        <Corner position="bottom-right" />
        
        <TitleContainer>
          <MainTitle>{t.title}</MainTitle>
          
          <MethodContainer>
            <MethodHighlight>A²</MethodHighlight>
          </MethodContainer>
          
          <SubtitleContainer>
            {clickCount >= 1 && (
              <SubtitlePart $fromLeft>{t.ads}</SubtitlePart>
            )}
            {clickCount >= 1 && clickCount >= 2 && <Separator />}
            {clickCount >= 2 && (
              <SubtitlePart>{t.service}</SubtitlePart>
            )}
          </SubtitleContainer>
        </TitleContainer>
      </ContentPreview>
    </SlideWrapper>
  );
};

export default PresentationSlide12;
